import { ft } from '../core/units.js';
import { EVENT } from './events.js';

/**
 * Who saw what, and how sure they are.
 *
 * This is the only place in the game that decides whether the player got away
 * with something. Nothing else may ask "was the player caught doing X" — not
 * the take, not the restricted zone, not the thrown bottle — because the moment
 * two systems answer that question they answer it differently, and a game where
 * being seen means one thing for a stolen linen and another for a smashed
 * bottle is a game nobody can form a plan in.
 *
 * So there is one input and one output. Everything that happens goes onto the
 * bus, the player's own conduct is offered every frame, and what comes out is a
 * list of *witness records*: this person, this thing, this sure.
 *
 * Confidence is three factors and no special cases:
 *
 *   how they know   watching it happen beats walking in on the aftermath beats
 *                   hearing it through a wall
 *   how far away    full marks at arm's length, down to a third at the edge of
 *                   what they can see or hear
 *   how long        a witness who keeps you in view after gets surer, which is
 *                   why walking away is worth doing and sprinting away is not
 *
 * A record is a *claim*, not a verdict. What anybody does about it is decided
 * elsewhere, by somebody who can weigh a 0.2 against a 0.9.
 */

/** How they came to know. Multiplied by distance, then grown by duration. */
const BASE = {
  saw: 0.55,
  after: 0.3,
  heard: 0.2,
};

/** Confidence left at the very edge of a sense. */
const EDGE = 0.35;

/**
 * And at the very edge of an *aftermath*, which is nothing at all.
 *
 * The difference is the difference between the two claims. Somebody at the
 * limit of their vision who watched a thing happen genuinely saw something —
 * a third of a claim is the right answer, and it is why EDGE is not zero.
 * Somebody who arrived afterwards and can only just make out the spot did not
 * walk in on anything; they are looking at a floor. Thirty-five hundredths of
 * a claim for that is a claim conjured out of distance, and across a property
 * this size it put faint rows on the after-action record for a bottle broken
 * two hundred feet away.
 */
const AFTERMATH_EDGE = 0;

/**
 * Seconds after an event during which walking in still counts as witnessing it.
 *
 * **Real seconds, and the distinction is the whole of this comment.** This file
 * holds two kinds of duration and they run on different clocks. How long a
 * person keeps turning something over is a fact about their day, so
 * `MEMORY_MINUTES` is game minutes — somebody who saw something at two o'clock
 * is not still on it at six. How long it takes to round a corner is a fact
 * about walking, and walking happens in `dt`.
 *
 * This is the second kind and it used to be stored as the first: `minutes() +
 * AFTERMATH / 60`, a tenth of a game minute, checked against the game clock.
 * The game clock runs at `scale` minutes per real second and the default scale
 * is one, so a window written as six seconds was **six frames** — and it got
 * shorter the faster the player ran the clock, measured at 0.33s of real time
 * at quarter speed, 0.10s at normal, 0.02s at four times, and nothing at all at
 * ten. At the top of the ladder the rule did not exist.
 *
 * Which made the sentence below untrue. "A witness who was across the corridor
 * when it happened and rounds the corner two seconds later has walked in on it"
 * — nobody rounds anything in six frames. The only people who ever got an
 * aftermath claim were the ones already standing in the right place at the
 * moment it happened, which is not walking in on it, it is watching it.
 *
 * So it is counted off `dt`, like the thing it models. Six seconds of somebody
 * walking, at any scale.
 */
const AFTERMATH = 6;

/**
 * Per second of continuing to have you in view, the most it can add, and how
 * long the window lasts.
 *
 * The window is not optional. Without it, a witness who keeps you in view gets
 * surer forever, and standing still in front of somebody — which is supposed to
 * be the alibi — walked every claim up to certainty. Staying in view right
 * after a thing happened does make a person surer it was you. Being looked at
 * ten minutes later does not.
 */
/**
 * Exported because the alibi is defined against it rather than beside it.
 *
 * This is the rate at which being looked at works *against* the player, and the
 * drain that is supposed to work for them has to beat it. The two were written
 * independently, both landed on six hundredths, and the exact tie pinned every
 * heavy claim in place for as long as it kept growing. `suspicion.js` now reads
 * this number and adds a margin to it, so the tie cannot come back.
 */
export const DURATION_GAIN = 0.06;
export const DURATION_CAP = 0.35;
const DURATION_SECONDS = 8;

/** Feet. What a person hears, unless their own row says otherwise. */
const HEARING_FEET = 30;

/** Records older than this are nobody's business any more. Game minutes. */
const MEMORY_MINUTES = 45;

/** Chest height, which is what a cone is aimed at and what a noise comes from. */
const CHEST = 1.2;

/**
 * `quiet` is the one thing a work class is allowed to say to this file.
 *
 * It scales the confidence of a claim about *the player helping themselves*
 * and nothing else — not a thrown bottle, not standing where you should not
 * be, not a colleague's own pocketing. One is what this file has always used,
 * so an untaught hand is witnessed exactly as it was before any of it existed.
 *
 * Deliberately not `belief`. That one is a fact about the witness and it
 * multiplies everything they think; this is a fact about one kind of act, and
 * keeping them apart is what stops a perk quietly making the player harder to
 * see doing anything at all.
 */
/*
 * `excused` is the other thing, and it is about events rather than claims.
 *
 * Something the job you are on sends you to do is not odd, however loud it
 * is: the golf cart with a lawn's carts behind it goes past the crew a dozen
 * times in an afternoon because that is the afternoon. The noise is still
 * made and still heard — the audio layer listens to the bus, not to this file
 * — it simply is not a thing anybody makes a note of. Whoever sent you decides
 * which events that covers; nothing is excused unless it says so.
 */
/*
 * `aboard` is the same idea for one witness rather than everybody: somebody
 * sitting in the cart is not a witness to the cart. A guest in the back for a
 * minute and a half down the cart road is being given a lift, not watching the
 * driver joyride past them, and nobody sat in a thing notes that it went by.
 * Asked with the event and the witness; false unless the caller says so.
 */
export function createPerception({
  npcs,
  bus,
  now,
  concealed = () => false,
  belief = () => 1,
  quiet = () => 1,
  excused = () => false,
  aboard = () => false,
}) {
  /** Keyed, so one person watching one thing is one record that grows. */
  const records = new Map();
  /** How long each NPC has had the player in continuous view. */
  const staring = new Map();
  /** And each thing that is not the player, by witness and by thing. */
  const eyeing = new Map();
  /** Where the player was last frame, for anything that asks who is about. */
  let player = null;
  /** Recent events still inside their aftermath window. */
  let pending = [];
  /** Acts counted, so each thrown or broken thing is its own claim. See `witness`. */
  let acts = 0;
  /**
   * Real seconds of world, accumulated off `dt`.
   *
   * The clock this file uses for anything that is really about people covering
   * ground. See `AFTERMATH`. It is not the hotel's clock and it must not become
   * it: the hotel's clock runs sixty times faster and the player can speed it
   * up again from the keyboard.
   */
  let seconds = 0;

  const minutes = () => (typeof now === 'function' ? now() : 0);

  function hearingOf(npc) {
    return ft(npc.data.hearing ?? HEARING_FEET);
  }

  /**
   * How far this person can see, asked of them rather than read off their row.
   *
   * The row is what they can see on a good day; what they can see *now* is the
   * body's business, and something in the world may have taken some of it away.
   * Falling back to the row keeps every stand-in that is only a data object —
   * the class at the pass builds four of those — working unchanged.
   */
  function sightRange(npc) {
    if (typeof npc.sightRange === 'number') return npc.sightRange;
    return npc.data.vision ? ft(npc.data.vision.range) : 0;
  }

  /**
   * Distance falls off linearly to EDGE at the limit of the sense, and stops
   * there. Linear rather than inverse-square because these are people noticing
   * things, not microphones.
   */
  function falloff(distance, reach, edge = EDGE) {
    if (reach <= 0) return 0;
    const t = Math.min(1, distance / reach);
    return 1 - (1 - edge) * t;
  }

  /**
   * Opens a record, or strengthens the one already open for that claim.
   *
   * The key is the person, the thing, and roughly where it happened — not a
   * fresh number each time. One event reaches this more than once on purpose:
   * a bottle breaking is a breakage *and* a noise, and both are true and both
   * go on the bus. What is not true is that the Banquet Manager formed two
   * separate beliefs about one bottle, which is what a counter gave us.
   */
  function note({ npc, what, detail, mode, position, key, confidence, subject = null }) {
    const id = key ?? `${npc.data.id}:${what}:${position.x.toFixed(1)},${position.z.toFixed(1)}`;
    const existing = records.get(id);
    if (existing) {
      existing.confidence = Math.max(existing.confidence, Math.min(1, confidence * belief()));
      existing.at = minutes();
      existing.mode = strongest(existing.mode, mode);
      return existing;
    }
    const record = {
      id,
      by: npc.data.id,
      department: npc.data.department,
      what,
      detail: detail ?? null,
      /*
       * What the claim is about, when it is not you.
       *
       * Null for everything this file has ever recorded, because until now
       * every claim was about the player — a thing they did, or a state they
       * were in. A sight is the first kind that is not, and it says so here
       * rather than by being a different kind of record: it is seen, weighed,
       * remembered and forgotten exactly like the rest, and the one question it
       * answers differently is whose card it could ever end up on.
       */
      subject,
      mode,
      confidence: Math.max(0, Math.min(1, confidence * belief())),
      opened: minutes(),
      at: minutes(),
      watchedFor: 0,
      grown: 0,
      position: { ...position },
    };
    records.set(id, record);
    return record;
  }

  const ORDER = { heard: 0, after: 1, saw: 2 };
  const strongest = (a, b) => (ORDER[b] > ORDER[a] ? b : a);

  /**
   * Whether this NPC can see a point, asked the way the cone wants it.
   *
   * Concealment enters here and nowhere else. It stops light and does nothing
   * about sound, so a hidden player is invisible to every cone in the building
   * and every bit as audible as they were a moment ago.
   */
  function canSee(npc, point) {
    if (point.concealed) return false;
    return npc.hasVision && npc.present && npc.sees(point, { eye: point.y + CHEST });
  }

  function distanceTo(npc, point) {
    return Math.hypot(point.x - npc.position.x, point.z - npc.position.z);
  }

  /**
   * Whether the aftermath itself is within reach of this person, and by which
   * sense — the range to measure the claim against, or zero for nothing.
   *
   * The rule this adds is one word long and the omission of it was a real bug:
   * the aftermath path used to ask only whether somebody could see *the
   * player*, and then hand them a claim about a thing that happened somewhere
   * else entirely. Six seconds is a short window and the property is two
   * hundred feet across, so a bottle smashed at the loading dock put a faint
   * row on the card of a manager standing in the lobby who had been looking at
   * you the whole time.
   *
   * Walking in on something has to mean walking in on *it*. So: line of sight
   * to where it happened, or close enough to have heard it — and the claim is
   * then measured against whichever sense actually reached, rather than
   * against a vision cone that never got there.
   */
  function aftermathReach(npc, at) {
    if (canSee(npc, at)) return sightRange(npc);
    const earshot = hearingOf(npc);
    return distanceTo(npc, at) <= earshot ? earshot : 0;
  }

  /**
   * One thing that happened. Everybody who was looking at it gets a record,
   * everybody within earshot gets a weaker one, and anybody who turns up in the
   * next few seconds gets a weaker one again.
   */
  function witness(event, { what, detail, radius, weight = 1, act = false }) {
    // Nothing to note: an excused event, or one weighted down to nothing.
    if (weight <= 0) return;
    /*
     * A thing done, as opposed to a state somebody is in or a noise that keeps
     * being made: each one is its own claim.
     *
     * Records are keyed by where a thing happened, which is right for being
     * somewhere and for a cart making the same noise lap after lap. It was
     * wrong for a thrown bottle: the second bottle thrown from where the first
     * one was thrown landed in the first one's record, so it was never
     * something they noticed again, and doing it again (the rule that turns
     * suspicious into sure) could not happen from a standing throw. Nobody who
     * watched you stand still and throw three could ever say so, and a guest
     * sat in one place watching you never could. Everybody who saw the one act
     * shares its key, so one bottle is still answered for once.
     */
    const key = act ? `${what}:#${(acts += 1)}` : null;
    const position = event.position ?? { x: 0, y: 0, z: 0 };
    /*
     * A thing the player did while out of sight was done out of sight. The
     * bottle they dropped under the table is under the table too, so it is
     * heard and not seen — which is the whole trade concealment offers, and it
     * has to hold for the event path as well as for the player's own body or
     * hiding under a table makes smashing things free.
     */
    const at = { x: position.x, y: position.y ?? 0, z: position.z, concealed: event.actor === 'player' && concealed() };
    const heardWithin = radius ?? 0;
    /*
     * Who did it, when it was not you.
     *
     * Every event used to be the player's, because nobody else did anything.
     * A colleague pocketing something is the first thing that is not, and it
     * is witnessed exactly like everything else — the one difference is the
     * record saying whose it is, which is the question whoever acts on it
     * asks first. They do not witness themselves.
     */
    const subject = event.actor && event.actor !== 'player' ? event.actor : null;

    for (const npc of npcs) {
      if (!npc.present || npc.data.id === subject) continue;
      if (event.cart && aboard(event, npc)) continue;
      const distance = distanceTo(npc, at);

      if (canSee(npc, at)) {
        note({
          npc,
          what,
          detail,
          subject,
          mode: 'saw',
          position: at,
          key: key && `${npc.data.id}:${key}`,
          confidence: BASE.saw * falloff(distance, sightRange(npc)) * weight,
        });
        continue;
      }

      const earshot = Math.min(hearingOf(npc), heardWithin || hearingOf(npc));
      if (heardWithin > 0 && distance <= earshot) {
        note({
          npc,
          what,
          detail,
          subject,
          mode: 'heard',
          position: at,
          key: key && `${npc.data.id}:${key}`,
          confidence: BASE.heard * falloff(distance, earshot) * weight,
        });
      }
    }

    // Held open so that somebody rounding the corner counts as having seen it,
    // and weighted the same as the moment itself: a take a class taught you to
    // make quietly is a take that leaves less behind to walk in on.
    pending.push({ what, detail, subject, at, until: seconds + AFTERMATH, seen: new Set(), weight, key });
  }

  return {
    /** Everything anybody currently believes, strongest first. */
    get witnesses() {
      return [...records.values()].sort((a, b) => b.confidence - a.confidence);
    },

    /** What one person believes. */
    by(npcId) {
      return [...records.values()].filter((record) => record.by === npcId);
    },

    /** The strongest claim anybody holds, or null. */
    get strongest() {
      let best = null;
      for (const record of records.values()) {
        if (!best || record.confidence > best.confidence) best = record;
      }
      return best;
    },

    /**
     * Whether one person can see a point that is not the player — a colleague,
     * say. The same cone and the same line of sight as everything above, so
     * there is still one answer to "can they see it".
     */
    looking(npcId, point) {
      const npc = npcs.find((candidate) => candidate.data.id === npcId);
      return Boolean(npc && canSee(npc, { x: point.x, y: point.y ?? 0, z: point.z }));
    },

    /** Whether anybody has the player in view this instant. */
    get watched() {
      return staring.size > 0;
    },

    /**
     * Whether one particular person has you, right now.
     *
     * The only way anything outside this module is allowed to ask. Every
     * `npc.sees` call in the game goes through here, so there is exactly one
     * answer to "can they see me" and everybody downstream gets the same one.
     */
    sees(npcId) {
      return staring.has(npcId);
    },

    /** Everyone who has you this instant. */
    get watchers() {
      return [...staring.keys()];
    },

    /** Seconds the longest-staring witness has had you. */
    get stared() {
      let most = 0;
      for (const seconds of staring.values()) most = Math.max(most, seconds);
      return most;
    },

    /**
     * Wires the bus. Every kind of event becomes the same kind of record; a
     * thrown bottle and a lifted stack of linen differ in the string, not in
     * the path.
     */
    listen() {
      const off = [
        bus.on(EVENT.BREAK, (event) =>
          witness(event, { what: 'break', detail: event.kind, radius: event.radius ?? ft(40), act: true })
        ),
        bus.on(EVENT.NOISE, (event) =>
          witness(event, {
            what: event.cause ?? 'noise',
            detail: event.kind ?? null,
            radius: event.radius ?? 0,
            weight: excused(event) ? 0 : 1,
          })
        ),
        bus.on(EVENT.THROW, (event) =>
          witness(event, { what: 'throw', detail: event.kind, radius: 0, act: true })
        ),
        /*
         * Its own line, next to the throw and deliberately not part of it. A
         * drop is seen if somebody is looking and heard if somebody is close,
         * exactly like everything else here — what makes it different is not
         * how it is perceived but what a witness is allowed to do with it, and
         * that is `suspicion.js`'s business rather than this file's.
         */
        bus.on(EVENT.DROP, (event) =>
          witness(event, { what: 'drop', detail: event.kind, radius: event.radius ?? 0 })
        ),
        /*
         * The one event a work class touches. `quiet` is 1 until somebody has
         * sat Quick Hands, and it applies only to the player's own take: a
         * colleague pocketing a pastry is witnessed at full strength whatever
         * the player has been taught.
         */
        /*
         * A prank is seen and heard exactly like everything else. Its own line
         * only because it carries its own word, so a witness's claim says what
         * they think they saw rather than calling a note on somebody's back a
         * theft.
         */
        bus.on(EVENT.PRANK, (event) =>
          witness(event, { what: 'prank', detail: event.prank, radius: 0 })
        ),
        bus.on(EVENT.TAKE, (event) =>
          witness(event, {
            what: 'take',
            detail: event.kind,
            radius: 0,
            weight: event.actor === 'player' ? quiet() : 1,
          })
        ),
      ];
      return () => off.forEach((stop) => stop && stop());
    },

    /**
     * The continuous half: where the player is and what is true about them.
     *
     * `conduct` is a list of things a witness could note — standing in a
     * restricted room, wearing the wrong uniform on the marble. They are not
     * events, they are states, so they build one record per witness that gets
     * surer the longer you stand there, which is exactly the shape of somebody
     * watching you and gradually deciding.
     */
    observe(dt, { position, conduct = [], sights = [] } = {}) {
      const at = { x: position.x, y: position.y ?? 0, z: position.z, concealed: concealed() };
      const stale = minutes();
      seconds += dt;
      player = at;

      /*
       * Things in the world that are not the player, and are a state rather
       * than an event: a herd standing by the dock doors is not something that
       * happened, it is something that is the case for as long as it is there.
       *
       * So it is conduct, pointed at a different body. The same cone, the same
       * falloff, the same growth the longer they look — counted per thing, so
       * the seconds somebody has spent watching you do not make them surer
       * about a javelina, and the other way round. Every sight says what it is
       * about, which is the one thing a conduct record never needed to say.
       */
      for (const npc of npcs) {
        const id = npc.data.id;
        for (const sight of sights) {
          const key = `${id}:${sight.key}`;
          const point = { x: sight.position.x, y: sight.position.y ?? 0, z: sight.position.z };
          if (!npc.present || !canSee(npc, point)) {
            eyeing.delete(key);
            continue;
          }
          const held = (eyeing.get(key) ?? 0) + dt;
          eyeing.set(key, held);
          note({
            npc,
            what: sight.what,
            detail: sight.detail ?? null,
            subject: sight.subject ?? sight.key,
            mode: 'saw',
            position: point,
            key,
            confidence:
              BASE.saw * falloff(distanceTo(npc, point), sightRange(npc)) +
              Math.min(DURATION_CAP, held * DURATION_GAIN),
          });
        }
      }
      // Something that has gone is not being looked at by anybody.
      if (eyeing.size) {
        const live = new Set(sights.map((sight) => sight.key));
        for (const key of eyeing.keys()) {
          if (!live.has(key.slice(key.indexOf(':') + 1))) eyeing.delete(key);
        }
      }

      for (const npc of npcs) {
        const id = npc.data.id;
        if (!npc.present || !canSee(npc, at)) {
          staring.delete(id);
          continue;
        }
        const held = (staring.get(id) ?? 0) + dt;
        staring.set(id, held);

        const distance = distanceTo(npc, at);
        const near = falloff(distance, sightRange(npc));
        const grown = Math.min(DURATION_CAP, held * DURATION_GAIN);

        for (const item of conduct) {
          note({
            npc,
            what: item.kind,
            detail: item.detail ?? null,
            mode: 'saw',
            position: at,
            key: `${id}:${item.kind}`,
            confidence: BASE.saw * near + grown,
          });
        }

        /*
         * Walking in on the aftermath. The event already happened; this person
         * was not looking then and is looking now — and can reach the thing
         * they are supposedly walking in on.
         *
         * Somebody out of reach is skipped rather than marked, on purpose. The
         * window is six seconds and people move: a witness who was across the
         * corridor when it happened and rounds the corner two seconds later has
         * walked in on it, which is the whole sentence this path exists to
         * mean. Marking them on the first frame they happened to be looking at
         * the player would shut that door before they got to it.
         */
        for (const recent of pending) {
          if (recent.seen.has(id) || recent.subject === id) continue;
          const reach = aftermathReach(npc, recent.at);
          const sure = reach
            ? BASE.after * falloff(distanceTo(npc, recent.at), reach, AFTERMATH_EDGE) * (recent.weight ?? 1)
            : 0;
          // Out of reach, or far enough into it to be worth nothing, is not
          // walking in on anything, and neither is marked. See above: the
          // window is six seconds and people cover ground inside it.
          if (sure <= 0) continue;
          recent.seen.add(id);
          note({
            npc,
            what: recent.what,
            detail: recent.detail,
            subject: recent.subject,
            mode: 'after',
            position: recent.at,
            key: recent.key && `${npc.data.id}:${recent.key}`,
            confidence: sure,
          });
        }

        // And a witness who keeps you in view gets surer about what they
        // already believe, whatever it was — for a while.
        for (const record of records.values()) {
          if (record.by !== id) continue;
          // Looking at you is not looking at the javelina.
          if (record.subject) continue;
          record.watchedFor = (record.watchedFor ?? 0) + dt;
          if (record.watchedFor > DURATION_SECONDS) continue;
          if ((record.grown ?? 0) >= DURATION_CAP) continue;
          const step = Math.min(DURATION_GAIN * dt, DURATION_CAP - (record.grown ?? 0));
          record.grown = (record.grown ?? 0) + step;
          record.confidence = Math.min(1, record.confidence + step);
        }
      }

      // On the walking clock, not the hotel's. See `AFTERMATH`.
      pending = pending.filter((recent) => recent.until > seconds);
      for (const [id, record] of records) {
        if (stale - record.at > MEMORY_MINUTES) records.delete(id);
      }
    },

    /**
     * Everybody within `reach` of a point, nearest first — the player and every
     * person on the floor, by the same measure.
     *
     * The other direction. Everything above is people perceiving; this is
     * people being perceived, by something that is not a person and has no
     * cone. A javelina does not look at you, it knows you are there, so there
     * is no facing and no line of sight in it — just how far, which is what the
     * wildlife table always measured the player by. The point of it living here
     * is the one this file exists for: there is one answer to "who is near
     * this", and the player and the Banquet Manager get it from the same line.
     *
     * Hidden or not makes no difference. Concealment stops light, and this is
     * not light.
     */
    near(at, reach) {
      const out = [];
      if (player) {
        const distance = Math.hypot(player.x - at.x, player.z - at.z);
        if (distance <= reach) out.push({ id: 'player', npc: null, position: player, distance });
      }
      for (const npc of npcs) {
        if (!npc.present) continue;
        const distance = distanceTo(npc, at);
        if (distance <= reach) out.push({ id: npc.data.id, npc, position: npc.position, distance });
      }
      return out.sort((a, b) => a.distance - b.distance);
    },

    /** Drops one claim. Used when somebody acts on it and is done with it. */
    close(id) {
      return records.delete(id);
    },

    clear() {
      records.clear();
      staring.clear();
      eyeing.clear();
      player = null;
      pending = [];
    },
  };
}
